/**
 * PortfoliYo Lab — application shell (IL-8.3).
 *
 * Deliberately not `components/Layout.tsx`. That one's nav lists eighteen destinations across
 * Portfolio, Analysis and Admin — Holdings, Orders, Scheduler, Settings — every one of them a
 * Trade page reached through a Trade route. Rendering it inside Lab would put dead links to
 * another application in a shell whose whole purpose is to be single-origin.
 *
 * So this is a Lab-sized shell: the three Lab-owned destinations, who is signed in, and — since
 * PB-4 — a real sign-out button. It used to be a link to Trade, because logging out deleted a row
 * from Trade-owned `sessions`; `lab_sessions` is Lab's, so the last thing this shell needed another
 * origin for is gone. It renders only for a signed-in user (the guard in `LabApp` sees to that), so
 * there is no signed-out state to handle here any more.
 */
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BarChart2, FlaskConical, ListMusic, Network, Scale, Shuffle } from 'lucide-react';
import { useLabAuth } from './LabAuthContext';

/** The Lab-owned pages. Every route below is served by the Lab process. */
const LAB_NAV = [
  // The Visualizer leads, ahead of authoring. Reading a definition is the step before writing one
  // and the only item here that asks nothing of you first: every other destination needs a saved
  // strategy to be about, and this one needs only text. It is also the cheapest place to send
  // somebody who arrives holding a definition and no account state at all.
  //
  // SDO-1 gave the lead to Strategies for the same kind of reason — authoring is where a research
  // session USED to start — and that argument now puts it second rather than retiring it.
  { path: '/visualizer', label: 'Visualizer', icon: Network },
  { path: '/strategies', label: 'Strategies', icon: ListMusic },
  { path: '/backtest', label: 'Backtest', icon: FlaskConical },
  { path: '/attribution', label: 'Attribution', icon: Scale },
  { path: '/allocation-analysis', label: 'Allocation Analysis', icon: BarChart2 },
  { path: '/adaptive', label: 'Adaptive Allocation', icon: Shuffle },
];

export function LabLayout() {
  const { user, logout } = useLabAuth();
  const navigate = useNavigate();

  const signOut = async () => {
    await logout();
    // Explicit rather than relying on the guard to bounce us: the guard's redirect carries a
    // `?next=` back to wherever we were standing, which is exactly wrong after a deliberate sign
    // out — the next person at this browser would land on the previous user's page.
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-4 py-3">
          <span className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <FlaskConical className="h-5 w-5 text-emerald-700" aria-hidden="true" />
            PortfoliYo Lab
          </span>

          <nav className="flex flex-1 flex-wrap items-center gap-1" aria-label="Lab sections">
            {LAB_NAV.map(({ path, label, icon: Icon }) => (
              <NavLink
                key={path}
                to={path}
                className={({ isActive }) =>
                  [
                    'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-emerald-50 text-emerald-800'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                  ].join(' ')
                }
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          {/* No loading or signed-out branch: this shell renders only inside `RequireLabAuth`, so
              a user is always present by the time it does. Both branches existed because Lab could
              not sign anyone in and had to render something for a visitor who was not. */}
          <span className="flex items-center gap-3 text-sm">
            <span className="text-slate-600">{user?.email}</span>
            {/* A button now, not a link out to Trade: PB-4 gave Lab `/api/auth/logout`, because
                the row it deletes is in `lab_sessions` and that table is Lab's. */}
            <button
              type="button"
              onClick={signOut}
              className="font-medium text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline"
            >
              Sign out
            </button>
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
